/*
 * A typescript & react boilerplate with api call example
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// import './debugger';
import {
	appWithBrowserRouter,
	ModuleFE_ActionProcessor,
	ModuleFE_Dialog,
	ModuleFE_LocalStorage,
	ModuleFE_Notifications,
	ModuleFE_StorageCleaner,
	ModuleFE_Window,
	ModuleFE_XHR,
	Storage_AppVersion,
	Thunder,
	ThunderConfig,
} from '@nu-art/thunderstorm-frontend/index';
import {App} from './app/App.js';
import {BeLogged, compareVersions, currentTimeMillis, Module} from '@nu-art/ts-common';
import {ModulePack_AdvisorStatic, ModulePackFE_Advisor} from '@app/advisor-frontend';
import {ModuleFE_Theme} from '@app/styles';
import {AdvisorApplicationName} from '@app/advisor-shared';
import {ModuleFE_SyncManager} from '@nu-art/thunderstorm-frontend/modules/sync-manager/ModuleFE_SyncManager';
import {ModuleFE_Workspace} from '@nu-art/ts-workspace-frontend/index';
import {ModuleFE_FirebaseListener} from '@nu-art/firebase-frontend/ModuleFE_FirebaseListener/ModuleFE_FirebaseListener';
import {HeaderKey_Application} from '@nu-art/thunderstorm-shared/headers';
import {ModulePackFE_AdvisorUI} from '@app/advisor-v5-frontend';
import {ModulePackFE_OpenTelemetry} from '@app/open-telemetry-frontend/index';
import {ModulePackFE_Coralogix} from '@app/coralogix-frontend/index';
import {ModuleFE_AppModule} from './app/modules/ModuleFE_AppModule/ModuleFE_AppModule.js';
import {ModuleFE_AdvisorATS} from '@app/artifact-ats-frontend/ats/ATS_ZippedCSV/module/ModuleFE_AdvisorATS';
import {ModuleFE_DBExplorer} from '@app/advisor-debug-frontend';
import {ModuleFE_Session, sessionContentJWT} from '@nu-art/user-account-frontend';
import {ModuleFE_Analytics} from '@nu-art/analytics-frontend';
import {defaultWorkspaceConfigs} from '@app/advisor-frontend/ui/workspace-configs';
import {LogClient_AdvisorConsoleProxy} from '@app/advisor-v5-frontend/logClients/LogClient_AdvisorConsoleProxy';
import {LogClient_AdvisorMemBuffer} from '@app/advisor-frontend/logClients/LogClient_AdvisorMemBuffer';
import appVersion from './version-app.json' with {type: 'json'};
import {deriveConfig} from './advisor-config.js';
import {ModuleFE_RuntimeEncounterFeed, ModuleFE_RuntimePertinentData} from '@app/advisor-v5-frontend/modules/runtime/index';

const modules: Module[] = [
	//TS
	ModuleFE_FirebaseListener,
	ModuleFE_XHR,
	ModuleFE_Theme,
	ModuleFE_Window,
	ModuleFE_Notifications,
	ModuleFE_ActionProcessor,
	ModuleFE_SyncManager,
	ModuleFE_LocalStorage,
	ModuleFE_Workspace,
	ModuleFE_Dialog,
	ModuleFE_Session,
	// app module
	ModuleFE_AppModule
];

ModuleFE_Session.setSessionKey(`session-jwt--${currentTimeMillis()}`);
ModuleFE_Session.sessionDecoder = sessionContentJWT;
ModuleFE_XHR.addDefaultHeader(HeaderKey_Application, () => AdvisorApplicationName);
ModuleFE_Workspace.setDefaultConfig({defaultConfigs: defaultWorkspaceConfigs});
ModuleFE_SyncManager.setShouldCleanIDBOnFullSync(false);
const prevAppVersion = Storage_AppVersion.get();
appVersion.version === '' && (appVersion.version = '0.0.0');

const config: ThunderConfig = deriveConfig();
const thunder = new Thunder(config);
thunder
	.addModulePack(modules)
	.addModulePack(ModulePackFE_OpenTelemetry)
	.addModulePack(ModulePackFE_Coralogix)
	.addModulePack(ModulePackFE_Advisor)
	.addModulePack(ModulePack_AdvisorStatic)
	.addModulePack(ModulePackFE_AdvisorUI)
	.addModulePack([ModuleFE_RuntimeEncounterFeed, ModuleFE_RuntimePertinentData])
	.addModulePack([ModuleFE_AdvisorATS, ModuleFE_Analytics, ModuleFE_DBExplorer])
	.setMainApp(App, appWithBrowserRouter)
	.addPreBuildAction(async () => {
		if (compareVersions(appVersion.version, prevAppVersion ?? '0.0.0') === -1) {
			await ModuleFE_StorageCleaner.cleanAll();
		}
		Storage_AppVersion.set(appVersion.version);
	})
	.build(async () => {
		// always capture recent logs in memory so we can attach them to bug reports
		BeLogged.addClient(LogClient_AdvisorMemBuffer);

		if (thunder.getConfig().enableLogs)
			BeLogged.addClient(LogClient_AdvisorConsoleProxy);
	});